import { create } from 'zustand';
import { DeliveryWindowStatisticsDTO, DeliveryWindowPageDTO } from '@api/generated/search-service/api';
import { deliveryWindowsManagementApi } from '@api/api-config';
import i18next from 'i18next';

interface DeliveryWindowsState {
  // Data
  statistics: DeliveryWindowStatisticsDTO | null;
  deliveryWindows: DeliveryWindowPageDTO | null;
  loading: boolean;
  error: string | null;

  // Pagination
  currentPage: number;
  pageSize: number;

  //
  organizationId: number | null;
  portalId: number | null;

  // Filters
  supplierId: number | null;
  configured: boolean | null; // null = všechny, true = s okny, false = bez oken
  supplierName: string;

  // Actions
  fetchStatistics: (organizationId: number, portalId: number) => Promise<void>;
  fetchDeliveryWindows: (
    organizationId: number,
    portalId: number,
    page?: number,
    size?: number,
    supplierId?: number,
    configured?: boolean | null,
    supplierName?: string,
  ) => Promise<void>;
  refresh: () => Promise<void> | undefined;
  clearError: () => void;
  resetFilters: () => void;
}

export const useDeliveryWindowsStore = create<DeliveryWindowsState>((set, get) => {
  return {
    // Initial state
    statistics: null,
    deliveryWindows: null,
    loading: false,
    error: null,
    currentPage: 0,
    pageSize: 20,
    supplierId: null,
    configured: null,
    supplierName: '',
    organizationId: null,
    portalId: null,

    // Actions
    fetchStatistics: async (organizationId: number, portalId: number) => {
      set({ loading: true, error: null });
      try {
        const response = await deliveryWindowsManagementApi.getDeliveryWindowStatistics({
          organizationId,
          portalId,
        });
        set({ statistics: response.data, loading: false });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : i18next.t('client:deliveryWindows.store.statsError'),
          loading: false,
        });
      }
    },

    fetchDeliveryWindows: async (
      organizationId: number,
      portalId: number,
      page = 0,
      size = 20,
      supplierId?: number,
      configured?: boolean | null,
      supplierName?: string,
    ) => {
      set({ loading: true, error: null });
      try {
        get().fetchStatistics(organizationId, portalId);
        const response = await deliveryWindowsManagementApi.getDeliveryWindows({
          organizationId,
          portalId,
          page,
          size,
          supplierId,
          configured: configured || undefined,
          supplierName: supplierName || undefined,
        });
        set({
          deliveryWindows: response.data,
          currentPage: page > 0 ? page : 0,
          pageSize: size,
          supplierId: supplierId || null,
          configured: configured || null,
          supplierName: supplierName || '',
          loading: false,
          organizationId,
          portalId,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : i18next.t('client:deliveryWindows.store.loadError'),
          loading: false,
        });
      }
    },

    refresh: () => {
      const { supplierName, organizationId, portalId, currentPage, pageSize, configured } = get();
      if (organizationId && portalId) return get().fetchDeliveryWindows(organizationId, portalId, currentPage, pageSize, undefined, configured, supplierName);
    },
    clearError: () => {
      set({ error: null });
    },

    resetFilters: () => {
      set({
        supplierId: null,
        configured: null,
        supplierName: '',
        currentPage: 0,
      });
    },
  };
});
